import { Accordion, AccordionDetails, AccordionGroup, AccordionSummary, Link } from '@mui/joy';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useState } from 'react';

import { AccordionSummaryHeading } from '@/components/content/AccordionSummaryHeading';
import { LegalHeading } from '@/components/legal/LegalHeading';
import { LegalText } from '@/components/legal/LegalText';
import { ObfuscatedEmail } from '@/components/legal/ObfuscatedEmail';
import { RouterLink } from '@/components/router/RouterLink';
import { makeMetaTitle } from '@/modules/META';

export const Route = createFileRoute('/_content/haeufige-fragen')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  head: () => ({
    meta: [makeMetaTitle(['Häufige Fragen'])],
  }),
});

const FAQ = [
  {
    sectionTitle: 'Allgemeine Informationen',
    questions: [
      {
        title: 'Was ist der RadMELDER BW?',
        content: (
          <LegalText>
            Der RadMELDER BW ist eine Online-Plattform, auf der Mängel und Hindernisse an der Radinfrastruktur in
            Baden-Württemberg gemeldet werden können. Die Meldungen werden automatisch an die zuständigen Behörden
            weitergeleitet. Das Angebot steht den Kommunen in Baden-Württemberg freiwillig und kostenlos zur Verfügung.
            Eine Meldungsabgabe ist nur in diesen Gebieten möglich.
          </LegalText>
        ),
      },
      {
        title: 'Wer kann den RadMELDER BW nutzen?',
        content: (
          <LegalText>
            Alle Bürger:innen können in <RouterLink to="/teilnehmende-kommunen">teilnehmenden Kommunen</RouterLink>{' '}
            Meldungen abgeben. Gemeinden, Landkreise und andere Behörden nutzen die Plattform zur Bearbeitung und
            Verwaltung der eingegangenen Hinweise.
          </LegalText>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Meldungsabgabe',
    questions: [
      {
        title: 'Wie funktioniert die Meldungsabgabe?',
        content: (
          <Fragment>
            <LegalText>
              Ein Rechtsklick auf die Karte setzt einen Meldungsort und öffnet das Meldungsformular. Auch ein Klick auf{' '}
              <b>Neue Meldung</b> öffnet das Meldungsformular. In einem schrittweisen Prozess können anschließend
              verschiedene Informationen zur Meldung eingegeben werden. Die Anwendung bietet mehrere Möglichkeiten, um
              einen Meldungsort festzulegen:
            </LegalText>
            <LegalText component="ul">
              <li>
                <b>Adresseingabe (amtliche Datenbasis)</b>
              </li>
              <li>
                <b>Punkt in Karte setzen</b>
              </li>
              <li>
                <b>Aktuelle GPS-Position (abhängig von Browser- und Systemberechtigungen)</b>
              </li>
              <li>
                <b>Standort einer Beschilderung Radnetz BW (QR-Code auf Pfosten bzw. Standort-Nr.)</b>
              </li>
              <li>
                <b>
                  Auswahl eines bestimmten Objekts durch Anwählen des Ortsauswahl-Chips (Meinen Sie vielleicht einen
                  dieser Orte? z.B. Servicestationen)
                </b>
              </li>
            </LegalText>
            <LegalText>
              Nach Angabe des Meldungsortes wird eine Duplikat-Erkennung gestartet und Meldungen am eingegebenen
              Meldungsort (Umkreis ca. 20 Meter) angezeigt. Sollte das Anliegen bereits in einer Meldung abgebildet
              sein, kann der jeweiligen Meldung zugestimmt werden. Damit erhöht sich der Prioritätswert bzw. das Voting
              für diese Meldung. Die Priorität wird in der Verwaltungsansicht angezeigt.
            </LegalText>
            <LegalText>
              Ergänzen Sie zur Meldungskategorie eine Meldungsbeschreibung und maximal drei Bilder, um die Bearbeitung
              zu erleichtern.
            </LegalText>
            <LegalText>
              Abschließend kann die eingegebene Meldung überprüft werden. Eine Bearbeitung Ihrer abgegebenen Meldung ist
              im Nachhinein nicht mehr möglich. Vor dem Senden der Meldung muss den Nutzungsbedingungen zugestimmt
              werden. Wenn Sie über den Fortschritt der Meldungsbearbeitung informiert werden wollen, kann optional eine
              E-Mail-Adresse angegeben werden, die anschließend für Benachrichtigungen genutzt wird.
            </LegalText>
          </Fragment>
        ),
      },
      {
        title: 'Was bedeutet die Option Beschilderung Radnetz BW mit Standortnummer?',
        content: (
          <LegalText>
            Die aufgestellten Schilder am Radnetz BW besitzen eine eindeutige Standortnummer und eine Banderole mit
            QR-Code. Mittels QR-Code-Scan oder Eingabe der Standortnummer kann eine Meldung zur Radnetz BW Beschilderung
            abgegeben werden. Meldungen dieses Typs unterscheiden sich zu den anderen Meldungen in Hinblick auf die
            Kategorieauswahl, da diese nur den Zustand der Radnetz BW Beschilderung betreffen.
          </LegalText>
        ),
      },
      {
        title: 'Kann ich eine Meldung anonym abgeben?',
        content: (
          <LegalText>
            Ja, eine Registrierung ist nicht erforderlich. Allerdings erhalten Nutzerinnen ohne Angabe einer
            E-Mail-Adresse keine Statusupdates zu ihrer Meldung und es entfällt die Möglichkeit für die Klärung von
            Rückfragen.
          </LegalText>
        ),
      },
      {
        title: 'Warum kann ich nicht überall eine Meldung abgeben?',
        content: (
          <Fragment>
            <LegalText>
              Der RadMELDER BW ist nur in Kommunen aktiv, die ihre Verwaltungsebene freigeschaltet haben. Falls eine
              Meldung in einem nicht freigeschalteten Gebiet erstellt wird, erscheint ein entsprechender Hinweis.
            </LegalText>
            <LegalText>
              Meldungen zur <b>Beschilderung RadNETZ BW</b> können im gesamten Bundesland abgegeben werden, unabhängig
              davon, ob eine Kommune am RadMELDER BW teilnimmt.
            </LegalText>
          </Fragment>
        ),
      },
      {
        title: 'Kann ich eine Meldung für eine gesamte Strecke abgeben?',
        content: (
          <LegalText>
            Derzeit sind nur punktuelle Meldungen möglich. Eine Funktion für liniengenaue Meldungen ist in Planung.
          </LegalText>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Bearbeitung und Zuständigkeit',
    questions: [
      {
        title: 'Was passiert nach dem Absenden einer Meldung?',
        content: (
          <LegalText>
            Die Meldung wird an die zuständige Stelle weitergeleitet. Die Verwaltung prüft das Problem, aktualisiert den
            Status und setzt – sofern möglich – entsprechende Maßnahmen um.
          </LegalText>
        ),
      },
      {
        title: 'Wie kann ich den Status meiner Meldung verfolgen?',
        content: (
          <LegalText>
            Wer eine E-Mail-Adresse angegeben hat, erhält automatische Benachrichtigungen bei Änderungen des
            Bearbeitungsstatus. Alternativ kann der Status direkt auf der Plattform eingesehen werden. Die zuständige
            Verwaltungsebene kann mithilfe öffentlich sichtbarer Kommentare zusätzliche Informationen zur Meldung
            eintragen.
          </LegalText>
        ),
      },
      {
        title: 'Wer ist für meine Meldung zuständig?',
        content: (
          <Fragment>
            <LegalText>
              Die Zuständigkeit richtet sich nach der Baulastträgerschaft am Radnetz, die in RadVIS gepflegt wird:
            </LegalText>
            <LegalText component="ul">
              <li>
                <b>Bundes- und Landesstraßen:</b> Land Baden-Württemberg oder Regierungspräsidien
              </li>
              <li>
                <b>Kommunale Radwege:</b> Städte und Gemeinden
              </li>
              <li>
                <b>Kreisstraßen mit Radwegen:</b> Landkreise
              </li>
            </LegalText>
            <LegalText>
              Falls unklar ist, wer zuständig ist, wird die Meldung intern weitergeleitet. Ist am Meldungsort keine
              Baulastträgerschaft in RadVIS hinterlegt, wird die Meldung zunächst der Gemeinde zugewiesen, die die
              Zuständigkeit prüft.
            </LegalText>
          </Fragment>
        ),
      },
      {
        title: 'Warum wird meine Meldung nicht bearbeitet oder abgelehnt?',
        content: (
          <LegalText>
            Manche Meldungen können nicht direkt bearbeitet werden, z.B. wenn die Zuständigkeit unklar ist oder eine
            Maßnahme langfristiger Planung bedarf. Die Verwaltung kann den Meldenden dazu eine Rückmeldung geben, sofern
            eine E-Mailadresse bei der Meldungsabgabe eingetragen wurde.
          </LegalText>
        ),
      },
      {
        title: 'Wie lange dauert die Bearbeitung meiner Meldung?',
        content: (
          <LegalText>
            Das hängt von der Art des Problems und der jeweiligen Behörde ab. Kleinere Mängel können schnell behoben
            werden, während größere Maßnahmen längere Planungen erfordern. Eine Bearbeitung aller Meldungen kann nicht
            garantiert werden.
          </LegalText>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Als Kommune am RadMELDER BW teilnehmen',
    questions: [
      {
        title: 'Wie können Kommunen den RadMELDER BW nutzen?',
        content: (
          <LegalText>
            Kommunen können sich registrieren und individuelle Einstellungen für ihren Bereich vornehmen, z. B.
            Zuständigkeiten verwalten und die Sichtbarkeit von Meldungen anpassen. Sie können sich als Kommune mit einem
            Service-BW-Account registrieren und ihre Verwaltungseinheit angeben. Nach einer Registrierung muss der
            Zugang vom RadMELDER-Team freigeschaltet werden. Zusätzlich können Landkreise ihre kreisangehörigen
            Gemeinden koordinieren und für die Meldungsabgabe freischalten. Bei Fragen wenden Sie sich gerne an{' '}
            <ObfuscatedEmail name="radmelder" />.
          </LegalText>
        ),
      },
      {
        title: 'Wie werden Meldungen innerhalb der Verwaltung weitergeleitet?',
        content: (
          <LegalText>
            Die Plattform nutzt hinterlegte Daten zur Baulastträgerschaft aus RadVIS, um Meldungen automatisch an die
            richtige Stelle zu leiten. Falls eine Meldung falsch zugewiesen wurde, kann sie innerhalb der Verwaltung
            weitergeleitet werden.
          </LegalText>
        ),
      },
      {
        title: 'Kann der RadMELDER BW mit bestehenden Meldesystemen der Kommunen verbunden werden?',
        content: (
          <LegalText>
            Ja, es gibt Schnittstellen zur Integration mit bestehenden kommunalen Meldesystemen. Falls keine direkte
            Anbindung via Open311 GeoReport V2 Standard möglich ist, können Meldungen auch per E-Mail weitergeleitet
            oder an ein bestehendes System verwiesen werden. Bei Interesse an einer Schnittstellenintegration können
            Kommunen Kontakt via <ObfuscatedEmail name="radmelder" /> aufnehmen.
          </LegalText>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Als Landkreis am RadMELDER BW teilnehmen',
    questions: [
      {
        title: 'Können Landkreise den RadMELDER BW nutzen?',
        content: (
          <LegalText>
            Ja. Neben Städten und Gemeinden können nun auch Landkreise den RadMELDER BW nutzen. Landkreise erhalten
            damit die Möglichkeit, den Einsatz des RadMELDER BW in ihrem Kreisgebiet selbstständig zu koordinieren und
            ihre kreisangehörigen Gemeinden für die Meldungsabgabe freizuschalten.
          </LegalText>
        ),
      },
      {
        title: 'Welche Rolle übernehmen Landkreise im RadMELDER BW?',
        content: (
          <Fragment>
            <LegalText>
              Landkreise können eine koordinierende Rolle für ihr Kreisgebiet übernehmen. Sie können festlegen, welche
              kreisangehörigen Gemeinden im RadMELDER BW aktiv sind, die eingehenden Meldungen überblicken und die
              Bearbeitung innerhalb des Kreisgebietes organisieren.
            </LegalText>
            <LegalText>
              Je nach Abstimmung vor Ort können Meldungen entweder durch die jeweilige Gemeinde selbst bearbeitet oder
              durch den Landkreis zentral koordiniert und bearbeitet werden.
            </LegalText>
          </Fragment>
        ),
      },
      {
        title: 'Können Landkreise im RadMELDER BW Kommunen freischalten?',
        content: (
          <Fragment>
            <LegalText>
              Ja. Landkreise können kreisangehörige Kommunen innerhalb ihres Kreisgebietes selbstständig für den
              RadMELDER BW freischalten und wenn gewünscht auch die Bearbeitung der Meldungen pro Kommune übernehmen.
              Erst nach Freischaltung der Kommune ist eine Meldungsabgabe im jeweiligen Gemeindegebiet möglich.
            </LegalText>
            <LegalText>
              Alternativ können sich Gemeinden weiterhin selbst registrieren und direkt für den RadMELDER BW
              freischalten lassen.
            </LegalText>
          </Fragment>
        ),
      },
      {
        title: 'Sind Meldungen automatisch im gesamten Landkreis möglich, sobald ein Landkreis teilnimmt?',
        content: (
          <LegalText>
            Nein. Auch wenn ein Landkreis den RadMELDER BW nutzt, sind Meldungen nur in den Gemeinden möglich, die für
            die Meldungsabgabe freigeschaltet sind.
          </LegalText>
        ),
      },
      {
        title: 'Können Landkreise kommunale Meldungen in ihrem Kreisgebiet einsehen?',
        content: (
          <LegalText>
            Freigeschaltete Landkreise können kommunale Meldungen in ihrem Kreisgebiet nur dann einsehen, wenn sie für
            die Meldungsbearbeitung der jeweiligen Kommune übergeordnet verantwortlich sind. Sie erhalten damit einen
            Überblick über Rückmeldungen zur Radinfrastruktur und können die Bearbeitung mit den kreisangehörigen
            Gemeinden koordinieren. Eine Meldungsabgabe ist nur in den Gemeinden möglich, die für den RadMELDER BW
            freigeschaltet sind.
          </LegalText>
        ),
      },
      {
        title: 'Können Landkreise Meldungen an Gemeinden weitergeben?',
        content: (
          <LegalText>
            Ja. Landkreise können eingehende Meldungen manuell an die zuständigen Gemeinden weitergeben bzw. diesen zur
            Bearbeitung zuweisen. Damit können Landkreise die Bearbeitung im Kreisgebiet koordinieren, auch wenn die
            fachliche Zuständigkeit bei einer Gemeinde liegt.
          </LegalText>
        ),
      },
      {
        title: 'Müssen Gemeinden ihre Meldungen selbst bearbeiten?',
        content: (
          <LegalText>
            Nicht zwingend. Innerhalb eines Landkreises kann vor Ort abgestimmt werden, ob die Gemeinden ihre Meldungen
            selbst bearbeiten oder ob der Landkreis die Meldungen zentral koordiniert bzw. bearbeitet. Die konkrete
            Organisation erfolgt zwischen Landkreis und kreisangehörigen Gemeinden.
          </LegalText>
        ),
      },
      {
        title: 'Können auch Meldungen zur wegweisenden Beschilderung der Landkreise abgegeben werden?',
        content: (
          <Fragment>
            <LegalText>
              Die Möglichkeit, Meldungen zur wegweisenden Beschilderung der Landkreise abzugeben, wird derzeit
              vorbereitet. Voraussetzung dafür ist, dass die entsprechenden Beschilderungsdaten in RadVIS integriert
              sind. Der RadMELDER kann diese Daten anschließend aus RadVIS übernehmen und für die Meldungsabgabe nutzbar
              machen.
            </LegalText>
            <LegalText>
              Bis zur Integration der jeweiligen Beschilderungsdaten in RadVIS ist die Meldungsabgabe zur wegweisenden
              Beschilderung nur als einfache Meldung (Adresse, Ort, Position) über die Kategorie „Beschilderung“
              möglich. Die Zuweisung dieser Meldungen erfolgt systemseitig derzeit standardmäßig an die Kommunen!
            </LegalText>
          </Fragment>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Technische Fragen',
    questions: [
      {
        title: 'Welche Wege sind im RadMELDER BW als Radwege hinterlegt?',
        content: (
          <Fragment>
            <LegalText>
              Die Plattform nutzt Daten aus dem offiziellen Radverkehrs-Infrastruktur-System RadVIS. Pink markierte
              Routen in der Karte sind als Radwege klassifiziert und umfassen:
            </LegalText>
            <LegalText component="ul">
              <li>
                <b>RadNETZ BW</b> (Alltag, Freizeit, Zielnetz)
              </li>
              <li>
                <b>Kommunale Radnetze</b> (Alltag und Freizeit)
              </li>
              <li>
                <b>Kreisweite Radnetze</b> (Alltag und Freizeit)
              </li>
              <li>
                <b>Radschnellverbindungen</b>
              </li>
            </LegalText>
            <LegalText>
              Nicht klassifizierte Wege werden in der Karte nicht speziell dargestellt. Eine Meldung ist an jeder
              Position innerhalb freigeschalteter Gebiete möglich.
            </LegalText>
          </Fragment>
        ),
      },
      {
        title: 'Welche Browser werden unterstützt?',
        content: (
          <LegalText>
            Der RadMELDER BW funktioniert mit aktuellen Versionen von Chrome, Firefox, Edge und Safari.
          </LegalText>
        ),
      },
      {
        title: 'Gibt es eine mobile App für den RadMELDER BW?',
        content: (
          <LegalText>
            Es gibt keine eigene App, aber die Plattform ist für die mobile Nutzung optimiert und kann über den
            Smartphone-Browser genutzt werden.
          </LegalText>
        ),
      },
      {
        title: 'Warum werden Karten-Symbole oder Funktionen nicht korrekt angezeigt?',
        content: (
          <LegalText>
            Falls Symbole oder Karteninhalte fehlen, hilft oft ein Neuladen der Seite. Falls das Problem weiterhin
            besteht, sollte ein anderer Browser getestet werden.
          </LegalText>
        ),
      },
    ],
  },
  {
    sectionTitle: 'Datenschutz und Sicherheit',
    questions: [
      {
        title: 'Welche Daten werden gespeichert?',
        content: (
          <LegalText>
            Die Plattform speichert die Meldungen und – falls angegeben – eine E-Mail-Adresse für Statusupdates.
            Personenbezogene Daten werden nicht öffentlich sichtbar gemacht. Details zur Datennutzung finden sie unter{' '}
            <Link component={RouterLink} to="/datenschutz">
              Datenschutz
            </Link>{' '}
            und{' '}
            <Link component={RouterLink} to="/nutzungsbedingungen">
              Nutzungsbedingungen
            </Link>
            .
          </LegalText>
        ),
      },
      {
        title: 'Sind meine Meldungen öffentlich einsehbar?',
        content: (
          <LegalText>
            Das hängt von den Einstellungen der Kommune ab. Manche Meldungen sind öffentlich sichtbar, andere nur für
            die Verwaltung.
          </LegalText>
        ),
      },
      {
        title: 'Wie schützt der RadMELDER BW meine Daten?',
        content: (
          <LegalText>
            Die Datenübertragungen erfolgen über verschlüsselte Verbindungen. Die Plattform entspricht den
            Datenschutzbestimmungen der DSGVO. Nähere Informationen finden Sie unter{' '}
            <Link component={RouterLink} to="/datenschutz">
              Datenschutz
            </Link>{' '}
            und{' '}
            <Link component={RouterLink} to="/nutzungsbedingungen">
              Nutzungsbedingungen
            </Link>
            .
          </LegalText>
        ),
      },
      {
        title: 'Wie finde ich heraus, ob meine Kommune den RadMELDER BW nutzt?',
        content: (
          <LegalText>
            <Link component={RouterLink} to="/teilnehmende-kommunen">
              Teilnehmende Kommunen
            </Link>{' '}
            können auf der Plattform eingesehen werden. Zudem kann in der Layerkontrolle der Karte die Ebene
            Teilnehmende Kommunen aktiviert werden.
          </LegalText>
        ),
      },
      {
        title: 'Warum wird meine Meldung nicht auf der Karte angezeigt?',
        content: (
          <LegalText>
            Jede Kommune entscheidet selbst, ob eine Meldung bzw. welche Meldungsdetails öffentlich sichtbar sind. Die
            Sichtbarkeit sagt nichts über den Bearbeitungsstand aus.
          </LegalText>
        ),
      },
    ],
  },
];

function RouteComponent() {
  const [index, setIndex] = useState<string>('');

  return (
    <Fragment>
      <LegalHeading level="h1" sx={{ marginTop: 0 }}>
        Häufige Fragen
      </LegalHeading>

      <RouterLink level="h4" to="/schnellstart">
        Schnelleinstieg RadMELDER
      </RouterLink>

      {FAQ.map((section, sectionIndex) => (
        <Fragment key={section.sectionTitle}>
          <LegalHeading level="h2">{section.sectionTitle}</LegalHeading>
          <AccordionGroup>
            {section.questions.map((question, questionIndex) => {
              const questionKey = `${sectionIndex}-${questionIndex}`;

              return (
                <Accordion
                  key={questionKey}
                  expanded={index === questionKey}
                  onChange={(_event, expanded) => {
                    setIndex(expanded ? questionKey : '');
                  }}
                >
                  <AccordionSummary>
                    <AccordionSummaryHeading>{question.title}</AccordionSummaryHeading>
                  </AccordionSummary>
                  <AccordionDetails>{question.content}</AccordionDetails>
                </Accordion>
              );
            })}
          </AccordionGroup>
        </Fragment>
      ))}
    </Fragment>
  );
}
